/**
 * Editable List Block Renderer
 * Renders list management interfaces with add/remove/edit capabilities
 */

import React from "react";
import { EditableStringList } from "@strivelabs/ui-blocks";
import type { UIBlock, UIActionEvent } from "@/types/ui-blocks";

export interface EditableListBlockRendererProps {
  block: UIBlock;
  onUIAction: (event: UIActionEvent) => void;
  /** When true, the component is disabled */
  readOnly?: boolean;
}

export function EditableListBlockRenderer({
  block, 
  onUIAction,
  readOnly = false
}: EditableListBlockRendererProps) {
  const [disabled, setDisabled] = React.useState(readOnly);
  if (block.type !== 'editable_list') return null;
  const { content } = block;

  const handleApply = (data: string[] | Array<{ label?: string; items: string[] }>) => {
    // Determine the mode and structure the response accordingly
    if (disabled) return;

    if (content.mode === "grouped") {
      onUIAction({
        type: "ui_action",
        component: "editable_list",
        label: content.label || "Editable List",
        state: {
          mode: "grouped",
          groups: data as Array<{ label?: string; items: string[] }>,
          confirmed: true
        }
      });
    } else {
      onUIAction({
        type: "ui_action",
        component: "editable_list",
        label: content.label || "Editable List",
        state: {
          mode: "string",
          items: data as string[],
          confirmed: true
        }
      });
    }
    
    setDisabled(true);
  };

  const handleCancel = () => {
    // For cancel, we don't emit any action - just let the component reset
    console.log("Editable list cancelled");
    if (disabled) return;
    setDisabled(true);
  };

  // Render based on mode
  if (content.mode === "grouped") {
    return (
      <div className="space-y-2">
        {content.label && (
          <h4 className="text-sm font-medium">{content.label}</h4>
        )}
        <EditableStringList
          mode="grouped"
          groups={content.groups}
          onApply={handleApply}
          onCancel={handleCancel}
          allowAdd={content.allowAdd}
          allowRemove={content.allowRemove}
          inputPlaceholder={content.inputPlaceholder}
          addButtonText={content.addButtonText}
          applyButtonText={content.applyButtonText}
          cancelButtonText={content.cancelButtonText}
          disabled={disabled}
        />
      </div>
    );
  }

  // Default to string mode
  return (
    <div className="space-y-2">
      {content.label && (
        <h4 className="text-sm font-medium">{content.label}</h4>
      )}
      <EditableStringList
        mode="string"
        items={content.items}
        onApply={handleApply}
        onCancel={handleCancel}
        allowAdd={content.allowAdd}
        allowRemove={content.allowRemove}
        inputPlaceholder={content.inputPlaceholder}
        addButtonText={content.addButtonText}
        applyButtonText={content.applyButtonText}
        cancelButtonText={content.cancelButtonText}
        disabled={disabled}
      />
    </div>
  );
}
